import React, { useEffect, useState } from 'react';
import {
    Box,
    TextField,
    Button,
    Typography,
    Grid,
    Paper,
    Snackbar,
    Alert,
    CircularProgress,
    MenuItem,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
} from '@mui/material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import SubTitle from '../common/SubTitle';
import config from '../../config.json';
import axios from 'axios';
import Upload_Icon from "../../images/vehicle/Upload_icon.svg";
import TitleBar from '../navbar/TitleBar';
import NavMenu from '../navbar/NavMenu';
import { Country, State } from 'country-state-city';
import RejectModal from './RejectModal';

axios.defaults.withCredentials = true;

export default function OrganizationUpdate() {
    const { id } = useParams();
    const location = useLocation();
    const { state: navigationState } = location; // Get state from navigation
    const currentPath = location.pathname;
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        name: '',
        domain: '',
        country: '',
        city: '',
        state: '',
        address: '',
        pincode: '',
        contactName: '',
        contactEmail: '',
        contactPhone: '',
        contactEmployeeId: '',
        contactDepartment: '',
        contactDesignation: '',
        userId: null,
    });
    const [loading, setLoading] = useState(false);
    const [isEditMode, setIsEditMode] = useState(!!id);
    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'success',
    });

    // Add state for country and state dropdown options
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState(null);

    // Check if the current path is a request view
    const isRequestView = currentPath.startsWith('/request/view/');

    // Track approval status
    const [approvalStatus, setApprovalStatus] = useState('');

    // States for rejection dialog
    const [rejectModalOpen, setRejectModalOpen] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    const [isActionLoading, setIsActionLoading] = useState(false);
    const [orgNameToReject, setOrgNameToReject] = useState(navigationState?.selectedOrganization?.name || '');

    // Load countries on component mount
    useEffect(() => {
        setCountries(Country.getAllCountries());
    }, []);

    // Update states when country changes
    useEffect(() => {
        if (selectedCountry) {
            setStates(State.getStatesOfCountry(selectedCountry.isoCode));
        } else {
            setStates([]);
        }
    }, [selectedCountry]);

    useEffect(() => {
        const fetchUserAndOrgData = async () => {
            const storedUserData = localStorage.getItem('userState');
            if (!storedUserData) {
                setNotification({
                    open: true,
                    message: 'User not authenticated. Please log in.',
                    severity: 'error',
                });
                navigate('/login');
                return;
            }

            const parsedUserData = JSON.parse(storedUserData);
            const userId = parsedUserData.user.id;
            setFormData(prev => ({ ...prev, userId }));

            // Check if organization data was passed in navigation state
            if (navigationState && navigationState.selectedOrganization) {
                console.log('Using organization data from navigation state:', navigationState.selectedOrganization);

                const organizationData = navigationState.selectedOrganization;

                // Find the country object based on name
                const countryObj = Country.getAllCountries().find(
                    country => country.name === organizationData.country
                );

                if (countryObj) {
                    setSelectedCountry(countryObj);
                }

                // Set approval status
                if (organizationData.approvalStatus) {
                    setApprovalStatus(organizationData.approvalStatus);
                }

                setFormData({ ...organizationData, userId });
                setLoading(false);
                return;
            }

            // If no data in navigation state, fetch from API
            if (id) {
                try {
                    setLoading(true);
                    const response = await axios.get(`${config.baseURL}/organization/${id}`);
                    const organizationData = response.data.organization || response.data;

                    console.log('Fetched organization data from API:', organizationData);

                    // Find the country object based on name
                    const countryObj = Country.getAllCountries().find(
                        country => country.name === organizationData.country
                    );

                    if (countryObj) {
                        setSelectedCountry(countryObj);
                    }

                    // Set approval status if it exists
                    if (organizationData.approvalStatus) {
                        setApprovalStatus(organizationData.approvalStatus);
                    }

                    setFormData({ ...organizationData, userId });
                } catch (error) {
                    console.error('Error fetching organization data:', error);
                    setNotification({
                        open: true,
                        message: error.response?.data?.message || 'Failed to load organization data',
                        severity: 'error',
                    });
                    if (error.response?.status === 401 || error.response?.status === 403) {
                        navigate('/login');
                    }
                } finally {
                    setLoading(false);
                }
            }
        };

        fetchUserAndOrgData();
    }, [id, navigate, navigationState]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // Special handling for country selection
        if (name === 'country') {
            const countryObj = countries.find(country => country.name === value);
            setSelectedCountry(countryObj);
        }

        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            let response;
            if (isEditMode) {
                response = await axios.put(
                    `${config.baseURL}/organization/${id}`,
                    formData
                );
            } else {
                response = await axios.post(
                    `${config.baseURL}/organization/admin`,
                    formData
                );
            }

            setNotification({
                open: true,
                message: isEditMode
                    ? 'Organization updated successfully'
                    : 'Organization created successfully',
                severity: 'success',
            });

            navigate('/org');
        } catch (error) {
            console.error('Error saving organization:', error);
            setNotification({
                open: true,
                message: error.response?.data?.message || `Failed to ${isEditMode ? 'update' : 'create'} organization`,
                severity: 'error',
            });
            if (error.response?.status === 401 || error.response?.status === 403) {
                navigate('/login');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleCloseNotification = () => {
        setNotification(prev => ({ ...prev, open: false }));
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            console.log('Uploading file:', file);
            // Add upload logic here if needed
        }
    };

    const handleCancel = () => {
        const storedUserData = localStorage.getItem('userState');
        const userId = storedUserData ? JSON.parse(storedUserData).user.id : null;

        if (isEditMode) {
            setFormData(prev => ({ ...prev, userId }));
            navigate(-1)
        } else {
            setFormData({
                name: '',
                domain: '',
                country: '',
                city: '',
                state: '',
                address: '',
                pincode: '',
                contactName: '',
                contactEmail: '',
                contactPhone: '',
                contactEmployeeId: '',
                contactDepartment: '',
                contactDesignation: '',
                userId,
            });
            navigate(-1)
        }
    };

    const handleAssign = () => {
        // Create a navigation state object with the organization data
        const organizationData = {
            ...formData,
            approvalStatus // Include approval status
        };

        // Determine the correct path based on the current path
        const assignPath = isRequestView
            ? `/request/feature`
            : `/organization/feature/${id}`;

        // Navigate with state data
        navigate(assignPath, {
            state: {
                selectedOrganization: organizationData
            }
        });
    }

    const handleReject = (id) => {
        // Open the rejection modal
        setRejectModalOpen(true);
    };

    const confirmReject = async () => {
        setIsActionLoading(true);

        try {
            const orgId=navigationState.selectedOrganization.id;
            console.log("orgId",orgId)
            await axios.post(
                `${config.baseURL}/register/${orgId}/reject`,
                { reason: rejectionReason },
                { withCredentials: true }
            );
            setRejectModalOpen(false);
            setNotification({
                open: true,
                message: 'Organization rejected successfully',
                severity: 'success',
            });
            navigate('/admin');
        } catch (error) {
            console.error('Error rejecting organization:', error);
            setNotification({
                open: true,
                message: error.response?.data?.msg || 'Failed to reject organization',
                severity: 'error',
            });
            setRejectModalOpen(false);
        } finally {
            setIsActionLoading(false);
        }
    };

    const handleAccept = async () => {
        setIsActionLoading(true);
        try {
            const orgId=navigationState.selectedOrganization.id;
            await axios.post(
                `${config.baseURL}/register/${orgId}/accept`,
                {},
                { withCredentials: true }
            );
            setNotification({
                open: true,
                message: 'Organization approved successfully',
                severity: 'success',
            });
            navigate('/org');
        } catch (error) {
            console.error('Error approving organization:', error);
            setNotification({
                open: true,
                message: error.response?.data?.msg || 'Failed to approve organization',
                severity: 'error',
            });
        } finally {
            setIsActionLoading(false);
        }
    };

    const handleSave = async () => {
        setIsActionLoading(true);
        try {
            const orgId = navigationState?.selectedOrganization?.id || id;
            
            // Make API call to update organization data
            await axios.put(
                `${config.baseURL}/organization/${orgId}`,
                formData,
                { withCredentials: true }
            );
            
            setNotification({
                open: true,
                message: 'Organization details saved successfully',
                severity: 'success',
            });
        } catch (error) {
            console.error('Error saving organization details:', error);
            setNotification({
                open: true,
                message: error.response?.data?.msg || 'Failed to save organization details',
                severity: 'error',
            });
        } finally {
            setIsActionLoading(false);
        }
    };

    // Determine title, path, and action buttons based on the current URL
    let title, path, actionButtons;

    if (isRequestView) {
        title = "Organization";
        path = "Organization > New Request > View Details";

        // Different buttons based on approval status
        if (approvalStatus === "REJECTED") {
            actionButtons = (
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={handleCancel}
                        sx={{
                            minWidth: 100,
                            height: 55,
                            borderRadius: '8px',
                            background: '#F2F2F3',
                            border: '1px solid #414958',
                            color: "#1F242E",
                            fontFamily: "Roboto",
                            fontWeight: "500",
                            fontSize: "20px",
                            textTransform: "none",
                            // ":hover": { background: '#F2F2F3' },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={handleSave}
                        disabled={isActionLoading}
                        sx={{
                            minWidth: 100,
                            height: 55,
                            borderRadius: '8px',
                            background: '#F9E719',
                            border: "1px solid #414958",
                            color: "#111214",
                            fontFamily: "Roboto",
                            fontWeight: "500",
                            fontSize: "20px",
                            textTransform: "none",
                            ":hover": { background: '#F9E719' },
                        }}
                    >
                        {isActionLoading ? <CircularProgress size={24} /> : 'Save'}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleAccept}
                        disabled={isActionLoading}
                        sx={{
                            minWidth: 100,
                            height: 55,
                            borderRadius: '8px',
                            background: '#F9E719',
                            border: "1px solid #414958",
                            color: "#111214",
                            fontFamily: "Roboto",
                            fontWeight: "500",
                            fontSize: "20px",
                            textTransform: "none",
                            ":hover": { background: '#F9E719' },
                        }}
                    >
                        {isActionLoading ? <CircularProgress size={24} /> : 'Accept'}
                    </Button>
                </Box>
            );
        } else {
            actionButtons = (
                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                    <Button
                        variant="outlined"
                        onClick={handleReject}
                        disabled={isActionLoading}
                        sx={{
                            minWidth: 100,
                            height: 55,
                            borderRadius: '8px',
                            background: '#EE3B2B',
                            border: "1px solid #414958",
                            color: "#FFFFFF",
                            fontFamily: "Roboto",
                            fontWeight: "500",
                            fontSize: "20px",
                            textTransform: "none",
                            ":hover": { background: '#EE3B2B' },
                        }}
                    >
                        {isActionLoading ? <CircularProgress size={24} color="inherit" /> : 'Reject'}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={handleCancel}
                        sx={{
                            minWidth: 100,
                            height: 55,
                            borderRadius: '8px',
                            background: '#F2F2F3',
                            border: '1px solid #414958',
                            color: "#1F242E",
                            fontFamily: "Roboto",
                            fontWeight: "500",
                            fontSize: "20px",
                            textTransform: "none",
                            // ":hover": { background: '#F2F2F3' },
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={handleSave}
                        disabled={isActionLoading}
                        sx={{
                            minWidth: 100,
                            height: 55,
                            borderRadius: '8px',
                            background: '#F9E719',
                            border: "1px solid #414958",
                            color: "#111214",
                            fontFamily: "Roboto",
                            fontWeight: "500",
                            fontSize: "20px",
                            textTransform: "none",
                            ":hover": { background: '#F9E719' },
                        }}
                    >
                        {isActionLoading ? <CircularProgress size={24} /> : 'Save'}
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleAccept}
                        disabled={isActionLoading}
                        sx={{
                            minWidth: 100,
                            height: 55,
                            borderRadius: '8px',
                            background: '#F9E719',
                            border: "1px solid #414958",
                            color: "#111214",
                            fontFamily: "Roboto",
                            fontWeight: "500",
                            fontSize: "20px",
                            textTransform: "none",
                            ":hover": { background: '#F9E719' },
                        }}
                    >
                        {isActionLoading ? <CircularProgress size={24} /> : 'Accept'}
                    </Button>
                </Box>
            );
        }
    } else {
        title = isEditMode ? "Update Organization Profile" : "Add New Organization";
        path = isEditMode ? "Organization > Update Organization" : "Organization > Add Organization";
        actionButtons = (
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 2 }}>
                <Button
                    variant="outlined"
                    onClick={handleCancel}
                    sx={{
                        width: 122,
                        height: 55,
                        borderRadius: '8px',
                        background: '#F2F2F3',
                        border: '1px solid #414958',
                        color: "#1F242E",
                        fontFamily: "Roboto",
                        fontWeight: "500",
                        fontSize: "20px",
                        textTransform: "none",
                        ":hover": { background: '#F2F2F3' },
                    }}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    type="submit"
                    disabled={loading}
                    sx={{
                        width: 125,
                        height: 55,
                        borderRadius: '8px',
                        background: '#F9E719',
                        border: '1px solid #414958',
                        color: "#1F242E",
                        fontFamily: "Roboto",
                        fontWeight: "500",
                        fontSize: "20px",
                        textTransform: "none",
                        ":hover": { background: '#F9E719' },
                    }}
                >
                    {loading ? <CircularProgress size={24} /> : (isEditMode ? 'Update' : 'Submit')}
                </Button>
            </Box>
        );
    }

    if (loading) {
        return (<>
            <TitleBar />
            <NavMenu />
            <SubTitle
                title={title}
                path={path}
                track={isEditMode && isRequestView ? "Assign Features" : ""}
                onTrackClick={handleAssign}
            />
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, height: '100vh' }}>
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Loading...</Typography>
            </Box>
        </>
        );
    }

    return (
        <>
            <TitleBar />
            <NavMenu />
            {approvalStatus === "REJECTED" && (
                <Box
                    sx={{
                        backgroundColor: '#EE3B2B',
                        color: '#721C24',
                        height: "36px",
                        alignItems: "center",
                        display: "flex",
                        justifyContent: "center",
                        fontFamily: "Roboto",
                        fontWeight: 600,
                        fontSize: "24px",
                        color: "#FFFFFF",

                    }}
                >
                    Application Rejected
                </Box>
            )}
            <SubTitle
                title={title}
                path={path}
                track={isEditMode || isRequestView ? "Assign Features" : ""}
                onTrackClick={handleAssign}
            />
            <Box sx={{ margin: "0px 40px" }}>
                <Paper sx={{ borderRadius: '8px', overflow: 'hidden', padding: "20px" }}>
                    <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 1 }}>
                        <Box>
                            <Box sx={{ bgcolor: '#647490', color: 'white', p: 2, borderRadius: '8px', fontWeight: 'bold', fontSize: '1.2rem' }}>
                                Organization Details
                            </Box>
                            <Box sx={{ p: 3 }}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={4}>
                                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Organization Name *</Typography>
                                        <TextField
                                            required
                                            fullWidth
                                            name="name"
                                            value={formData.name}
                                            onChange={handleInputChange}
                                            placeholder="Enter Organization Name"
                                            variant="outlined"
                                            InputLabelProps={{ shrink: true }}
                                            type="text"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Website</Typography>
                                        <TextField
                                            fullWidth
                                            name="domain"
                                            value={formData.domain}
                                            onChange={handleInputChange}
                                            placeholder="Enter Website"
                                            variant="outlined"
                                            InputLabelProps={{ shrink: true }}
                                            type="text"
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Country *</Typography>
                                        <TextField
                                            select
                                            required
                                            fullWidth
                                            name="country"
                                            value={formData.country}
                                            onChange={handleInputChange}
                                            placeholder="Select Country"
                                            variant="outlined"
                                            InputLabelProps={{ shrink: true }}
                                            SelectProps={{
                                                MenuProps: {
                                                    PaperProps: {
                                                        style: {
                                                            maxHeight: 250,
                                                        },
                                                    },
                                                },
                                            }}
                                        >
                                            <MenuItem value="">
                                                <em>Select Country</em>
                                            </MenuItem>
                                            {countries.map((country) => (
                                                <MenuItem key={country.isoCode} value={country.name}>
                                                    {country.name}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} md={8}>
                                        <Grid container spacing={3}>
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="subtitle2" sx={{ mb: 1 }}>City</Typography>
                                                <TextField
                                                    fullWidth
                                                    name="city"
                                                    value={formData.city}
                                                    onChange={handleInputChange}
                                                    placeholder="Enter City"
                                                    variant="outlined"
                                                    InputLabelProps={{ shrink: true }}
                                                    type="text"
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="subtitle2" sx={{ mb: 1 }}>State *</Typography>
                                                <TextField
                                                    select
                                                    required
                                                    fullWidth
                                                    name="state"
                                                    value={formData.state}
                                                    onChange={handleInputChange}
                                                    placeholder="Select State"
                                                    variant="outlined"
                                                    InputLabelProps={{ shrink: true }}
                                                    disabled={!selectedCountry}
                                                    SelectProps={{
                                                        MenuProps: {
                                                            PaperProps: {
                                                                style: {
                                                                    maxHeight: 250,
                                                                },
                                                            },
                                                        },
                                                    }}
                                                >
                                                    <MenuItem value="">
                                                        <em>Select State</em>
                                                    </MenuItem>
                                                    {states.map((state) => (
                                                        <MenuItem key={state.isoCode} value={state.name}>
                                                            {state.name}
                                                        </MenuItem>
                                                    ))}
                                                </TextField>
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Address *</Typography>
                                                <TextField
                                                    required
                                                    fullWidth
                                                    name="address"
                                                    value={formData.address}
                                                    onChange={handleInputChange}
                                                    placeholder="Enter Address Line 1"
                                                    variant="outlined"
                                                    InputLabelProps={{ shrink: true }}
                                                    type="text"
                                                />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Pincode *</Typography>
                                                <TextField
                                                    required
                                                    fullWidth
                                                    name="pincode"
                                                    value={formData.pincode}
                                                    onChange={handleInputChange}
                                                    placeholder="Enter Pincode"
                                                    variant="outlined"
                                                    InputLabelProps={{ shrink: true }}
                                                    type="text"
                                                />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <Box
                                            sx={{
                                                border: '2px dashed #ccc',
                                                borderRadius: '8px',
                                                p: 2,
                                                height: '100%',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                justifyContent: 'center',
                                                alignItems: 'center',
                                                position: 'relative',
                                            }}
                                        >
                                            <img src={Upload_Icon} alt="Upload" style={{ width: 40, height: 40, marginBottom: 1 }} />
                                            <Typography variant="body1" sx={{ color: '#1F242E', fontWeight: 600 }}>Organization Icon</Typography>
                                            <Typography variant="body2" sx={{ color: '#666' }}>Click to upload or drag and drop</Typography>
                                            <Typography variant="body2" sx={{ color: '#666', fontSize: '12px' }}>PNG, JPG, GIF up to 10MB</Typography>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={handleImageUpload}
                                                style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    width: '100%',
                                                    height: '100%',
                                                    opacity: 0,
                                                    cursor: 'pointer',
                                                    zIndex: 1,
                                                }}
                                            />
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Box>

                        <Box>
                            <Box sx={{ bgcolor: '#647490', color: 'white', p: 2, borderRadius: '8px', fontWeight: 'bold', fontSize: '1.2rem' }}>
                                Contact Details
                            </Box>
                            <Box sx={{ p: 3 }}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Full Name *</Typography>
                                        <TextField
                                            required
                                            fullWidth
                                            name="contactName"
                                            value={formData.contactName}
                                            onChange={handleInputChange}
                                            placeholder="Enter Full Name"
                                            variant="outlined"
                                            InputLabelProps={{ shrink: true }}
                                            type="text"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Email *</Typography>
                                        <TextField
                                            required
                                            fullWidth
                                            name="contactEmail"
                                            value={formData.contactEmail}
                                            onChange={handleInputChange}
                                            placeholder="Enter Email"
                                            type="email"
                                            variant="outlined"
                                            InputLabelProps={{ shrink: true }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Phone Number *</Typography>
                                        <TextField
                                            required
                                            fullWidth
                                            name="contactPhone"
                                            value={formData.contactPhone}
                                            onChange={handleInputChange}
                                            placeholder="Enter Phone Number"
                                            variant="outlined"
                                            InputLabelProps={{ shrink: true }}
                                            type="tel"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Employee ID</Typography>
                                        <TextField
                                            fullWidth
                                            name="contactEmployeeId"
                                            value={formData.contactEmployeeId || ''}
                                            onChange={handleInputChange}
                                            placeholder="Enter Employee ID"
                                            variant="outlined"
                                            InputLabelProps={{ shrink: true }}
                                            type="text"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Department *</Typography>
                                        <TextField
                                            required
                                            fullWidth
                                            name="contactDepartment"
                                            value={formData.contactDepartment}
                                            onChange={handleInputChange}
                                            placeholder="Enter Department"
                                            variant="outlined"
                                            InputLabelProps={{ shrink: true }}
                                            type="text"
                                            disabled={isRequestView}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Designation *</Typography>
                                        <TextField
                                            required
                                            fullWidth
                                            name="contactDesignation"
                                            value={formData.contactDesignation}
                                            onChange={handleInputChange}
                                            placeholder="Enter Designation"
                                            variant="outlined"
                                            InputLabelProps={{ shrink: true }}
                                            type="text"
                                            disabled={isRequestView}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        </Box>

                        {actionButtons}
                    </Box>
                </Paper>
            </Box>

            {/* Rejection Modal */}
            <RejectModal
                open={rejectModalOpen}
                onClose={() => setRejectModalOpen(false)}
                onConfirm={confirmReject}
                isActionLoading={isActionLoading}
                organizationName={orgNameToReject}
            />
            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={handleCloseNotification}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={handleCloseNotification}
                    severity={notification.severity}
                    sx={{ width: '100%' }}
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </>
    );
}